import type { FC } from "react";

import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import Button from "~/atoms/buttons/Button";
import ButtonLoading from "~/atoms/buttons/ButtonLoading";

import type { ErrorFallbackProps } from "./types";

const ErrorFallback: FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  onRetry,
  mode = "detailed",
  boundaryName,
}) => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  if (mode === "minimal") {
    return (
      <div className="flex items-center justify-center rounded-lg border border-red-200 bg-red-50 p-4">
        <ExclamationTriangleIcon className="mr-2 h-5 w-5 text-red-500" />
        <span className="text-sm text-red-700">Something went wrong</span>
        <button
          onClick={handleRetry}
          className="ml-2 text-sm text-red-600 underline hover:text-red-800"
        >
          Try again
        </button>
      </div>
    );
  }

  if (mode === "fullpage") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <div className="text-center">
            <h1 className="mb-2 text-lg font-medium text-gray-900">
              Application Error
            </h1>
            <p className="mb-4 text-sm text-gray-600">
              Something unexpected happened. Please try refreshing the page or
              contact support if the problem persists.
            </p>
            <div className="flex justify-center space-x-2">
              <ButtonLoading
                onClick={handleRetry}
                startAdornment={<ArrowPathIcon className="mr-2 h-4 w-4" />}
              >
                Try Again
              </ButtonLoading>
              <Button
                onClick={() => (window.location.href = "/")}
                color="tertiary"
                variant="outlined"
              >
                Go to Homepage
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-6">
      <div className="flex items-start">
        <ExclamationTriangleIcon className="mt-1 mr-3 h-6 w-6 shrink-0 text-red-500" />
        <div className="flex-1">
          <h3 className="mb-2 text-lg font-medium text-red-800">
            Something went wrong
            {boundaryName && ` in ${boundaryName}`}
          </h3>
          <p className="mb-4 text-sm text-red-700">
            We encountered an unexpected error. Please try again or contact
            support if the issue persists.
          </p>

          {import.meta.env.VITE_APP_ENVIRONMENT === "development" && error && (
            <details className="mb-4">
              <summary className="mb-2 cursor-pointer text-sm font-medium text-red-800">
                Error Details (Development Only)
              </summary>
              <div className="overflow-auto rounded bg-red-100 p-3 font-mono text-xs text-red-900">
                <div className="mb-2">
                  <strong>Error:</strong> {error.message}
                </div>
                {error.stack && (
                  <div className="mb-2">
                    <strong>Stack:</strong>
                    <pre className="mt-1 whitespace-pre-wrap">
                      {error.stack}
                    </pre>
                  </div>
                )}
                {errorInfo?.componentStack && (
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="mt-1 whitespace-pre-wrap">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}

          <div className="flex justify-center space-x-2">
            <ButtonLoading
              onClick={handleRetry}
              startAdornment={<ArrowPathIcon className="mr-2 h-4 w-4" />}
            >
              Try Again
            </ButtonLoading>
            <Button
              onClick={() => {
                window.history.back();
                setTimeout(() => window.location.reload(), 100);
              }}
              color="tertiary"
              variant="outlined"
            >
              Go Back
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
